import React from 'react';
import type { EntitySignalRow } from '@shared/flow/entity-signals';
import { SALESFORCE_SYNC_ACTOR, SUGGESTED_STATUS } from '@shared/schemas/terra-signals';
import { resolveSignalTypeDisplay, resolveActionStatePill } from './signal-type-display';
import { WorkflowsDropdown } from '../contact/ContactActionMenus';
import { CompactUrlLink } from '../links';
import { fmtDateOnlyText } from '../account/accountFieldFormat';

/**
 * Row list for the "Actions" tab (plan
 * `plans/2026-09-22-contact-actions-intelligence-implementation-plan.md`
 * §2b item 2 + D1a) — one row per `terra_signals` row, shared by
 * `ContactTimeline`'s and `AccountTimeline`'s Actions mode chip.
 */

/** Drop empty parts, join the rest — matches ContactTimeline.tsx's own `join`. */
function join(...parts: (string | null | undefined)[]): string | null {
  const kept = parts.filter((p): p is string => !!p && p.trim() !== '');
  return kept.length ? kept.join(' · ') : null;
}

/** event_date is an instant (timestamptz): local short "Sep 15". due_date is a DATE — use `fmtDateOnlyText` with SHORT_DAY. */
const SHORT_DAY: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * "Actioned By" meta segment (D1a) — reuses the exact sentinel-handling
 * convention `ContactFlowTable.tsx`'s `signal_completed_by` renderer already
 * established for this same field: the raw `SALESFORCE_SYNC_ACTOR` sentinel
 * is never rep-facing, and renders as "Unknown" with a tooltip instead. Only
 * shown when the row actually carries a completer name — an open/undismissed
 * signal has no actioned-by yet.
 */
function ActionedBy({ name }: { name: string | null | undefined }) {
  if (!name) return null;
  if (name !== SALESFORCE_SYNC_ACTOR) {
    return <span>Actioned by {name}</span>;
  }
  return (
    <span>
      Actioned by <span title={SALESFORCE_SYNC_ACTOR}>Unknown</span>
    </span>
  );
}

/** Whether `RowAffordance` will render anything for this row — exported so a
 *  caller (e.g. ContactTimeline's accordion header) can skip an empty wrapper. */
export function hasActionAffordance(row: EntitySignalRow): boolean {
  const actionable = (row.action_state === 'To do' || row.signal_status === SUGGESTED_STATUS) && row.contact_id != null;
  return !!row.source_link || actionable;
}

/**
 * The affordance at the row's end. `source_link` wins whenever it's
 * present — the Signal Action arm and the Campaign arm's real-membership
 * rows always carry one (a live Salesforce record: `signal-action.ts`'s
 * `'https://clay.lightning.force.com/' || sa.id`, same shape in
 * `campaign.ts`), so a "GTM Play · Running" or otherwise already-fired row
 * links to the real thing instead of offering to re-enroll into it (2026-
 * 09-22, Spencer QA: a Running row still showed a bare Enroll button).
 * Only Terra-queue-only rows (`source_link: null` — nothing exists in
 * Salesforce yet) fall through to `WorkflowsDropdown`, which already
 * distinguishes "already queued" (a Queued button linking to the queue
 * sidebar) from "not yet" (the Enroll dropdown) on its own.
 */
export function RowAffordance({ row }: { row: EntitySignalRow }) {
  if (row.source_link) {
    return <CompactUrlLink url={row.source_link} label="Open" />;
  }
  const actionable = (row.action_state === 'To do' || row.signal_status === SUGGESTED_STATUS) && row.contact_id != null;
  if (actionable) {
    return (
      <WorkflowsDropdown
        contactId={row.contact_id!}
        contactName={row.contact_name}
        contactEmail={null}
      />
    );
  }
  return null;
}

/**
 * Exported so ContactTimeline/AccountTimeline can merge action rows directly
 * into the main chronological entry list (not a separate mode/chip — Spencer
 * QA feedback 2026-09-22: actions must interleave with everything else).
 * Renders its own bordered row wrapper, matching the generic entry rows'
 * `border-b` styling, so it drops into that list without an extra wrapper.
 */
export function ActionRow({ row }: { row: EntitySignalRow }) {
  const display = resolveSignalTypeDisplay(row.signal_type, row.signal_subtype);
  const { Icon } = display;
  const dateLabel = row.event_date ? fmtDate(row.event_date) : (fmtDateOnlyText(row.due_date, SHORT_DAY) ?? '—');
  const typeLabel = join(display.label, row.signal_subtype) ?? display.label;
  const metaParts = [
    row.signal_status,
    row.due_date ? `Due ${fmtDateOnlyText(row.due_date, SHORT_DAY)}` : null,
    row.assignee,
    row.source_system,
  ];

  return (
    <div className="flex gap-2 py-1.5 text-[12px] border-b border-primary/40 dark:border-nightshade-800 last:border-b-0">
      <span className="w-11 shrink-0 text-[11px] text-content-tertiary dark:text-nightshade-500 pt-0.5">{dateLabel}</span>
      <span className={`w-[22px] h-[22px] shrink-0 rounded-md flex items-center justify-center ${display.tone}`}>
        <Icon className="w-3.5 h-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`px-1.5 py-0.5 rounded-md text-[9.5px] font-medium whitespace-nowrap truncate max-w-[140px] ${display.tone}`}>{typeLabel}</span>
          <span className={`px-1.5 py-0.5 rounded-full text-[9.5px] font-medium ${resolveActionStatePill(row.action_state)}`}>
            {row.action_state}
          </span>
        </div>
        <div className="font-medium text-content-primary dark:text-nightshade-100 truncate">{row.signal_name}</div>
        <div className="text-content-tertiary dark:text-nightshade-500 truncate">
          {join(...metaParts)}
          {row.action_completed_by_name && (
            <>
              {join(...metaParts) ? ' · ' : ''}
              <ActionedBy name={row.action_completed_by_name} />
            </>
          )}
        </div>
      </div>
      <div className="shrink-0 flex items-center">
        <RowAffordance row={row} />
      </div>
    </div>
  );
}

export interface ActionsTabListProps {
  rows: EntitySignalRow[];
  truncated: boolean;
  isLoading: boolean;
  error: unknown;
  emptyLabel: string;
}

export function ActionsTabList({ rows, truncated, isLoading, error, emptyLabel }: ActionsTabListProps) {
  if (isLoading) {
    return <div className="text-[12px] text-content-tertiary dark:text-nightshade-500 py-4 text-center">Loading actions…</div>;
  }
  if (error) {
    return <div className="text-[12px] text-content-danger dark:text-red-400 py-4 text-center">Actions failed to load.</div>;
  }
  if (rows.length === 0) {
    return <div className="text-[12px] text-content-tertiary dark:text-nightshade-500 py-4 text-center">{emptyLabel}</div>;
  }

  return (
    <div>
      {rows.map(row => <ActionRow key={row.signal_id} row={row} />)}
      {truncated && (
        <div className="text-[11px] text-content-tertiary dark:text-nightshade-500 pt-2 text-center">
          Showing {rows.length} — more exist
        </div>
      )}
    </div>
  );
}
